import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card, F0Dialog } from '@factorialco/f0-react'
import { Input, OneAlert, Textarea } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Info, Money, Plane } from '@factorialco/f0-react/icons/app'
import {
  CreateItManagementOrderDocument,
  type DistributorProductVariantsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import emptyStateImage from 'modules/it_management/pages/Empty-state-device-catalog-card.png'

type DistributorProductVariant =
  DistributorProductVariantsQuery['itManagement']['distributorProductVariantsConnection']['nodes'][number]

interface RequestDeviceModalProps {
  variant: DistributorProductVariant | null
  onClose: () => void
  onSuccess?: () => void
}

const formatPrice = (
  cost: number | string | null | undefined,
  period: number | null | undefined
): string => {
  if (!cost) return '-'
  const costNumber = typeof cost === 'string' ? parseFloat(cost) : cost
  if (isNaN(costNumber)) return '-'
  if (period && period > 0) {
    return `From ${costNumber.toFixed(2)} € /month`
  }
  return `${costNumber.toFixed(2)} €`
}

const formatPeriod = (period: number | null | undefined): string | undefined => {
  if (!period || period <= 0) return undefined
  return i18n.t('it_management.device_catalog.detail.period_months', { count: period })
}

const RequestDeviceModal: React.FC<RequestDeviceModalProps> = ({ variant, onClose, onSuccess }) => {
  const { email, company, employee, access } = useCurrentSession()
  const [requestEmail, setRequestEmail] = React.useState(email || '')
  const [notes, setNotes] = React.useState('')

  const [createOrder] = useMutation(CreateItManagementOrderDocument, {
    extractorFn: (data) => data.itManagementMutations.createItmanagementOrder,
  })

  const handleClose = React.useCallback(() => {
    if (variant) {
      track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceClose, {
        device_id: variant.id,
        company_id: company.id,
        user_id: access.id,
      })
    }
    onClose()
  }, [variant, company.id, access.id, onClose])

  React.useEffect(() => {
    if (!variant) return
    const style = document.createElement('style')
    style.textContent = `
      [data-radix-portal] > div:has([role="dialog"]) {
        max-width: 820px !important;
        width: 820px !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
    }
  }, [variant])

  if (!variant) return null

  const deviceName = variant.product?.name
    ? `${variant.product.brand || ''} ${variant.product.name}`.trim()
    : variant.sku || variant.id

  const price = formatPrice(variant.firstOfferCost, variant.firstOfferPeriod)
  const period = formatPeriod(variant.firstOfferPeriod)
  const deliveryInfo =
    variant.deliveryConditions && variant.deliveryConditions !== '-'
      ? variant.deliveryConditions
      : i18n.t('it_management.device_catalog.confirmed_delivery_message')
  const deviceImage = variant.firstProductMediaImageUrl
    ? buildUrl(variant.firstProductMediaImageUrl)
    : emptyStateImage

  const handleSubmit = async () => {
    if (!company || !variant || !employee) return

    track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceSubmit, {
      device_id: variant.id,
      device_name: deviceName,
      has_message: notes.trim().length > 0,
      company_id: company.id,
      user_id: access.id,
    })

    app.setProgress({
      message: i18n.t('it_management.device_catalog.request_modal.creating_order'),
    })

    try {
      const result = await createOrder({
        variables: {
          companyId: company.id,
          variationId: variant.id,
          distributorOfferId: undefined,
          employeeId: employee.id,
          quantity: 1,
          email: requestEmail,
          notes: notes.trim() || '-',
        },
      })

      app.setProgress(null)

      let errorMessage = 'Unknown backend error'
      const handled = handleResult(result, ({ message }) => {
        errorMessage = message || 'Unknown backend error'
        app.setFlash({
          type: 'wrong',
          message:
            message || i18n.t('it_management.device_catalog.request_modal.failed_to_create_order'),
        })
      })

      if (handled.success) {
        track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceSuccess, {
          device_id: variant.id,
          company_id: company.id,
          user_id: access.id,
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('it_management.device_catalog.request_modal.order_created_successfully'),
        })
        onSuccess?.()
        onClose()
      } else {
        track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceError, {
          device_id: variant.id,
          error_type: 'backend_error',
          error_message: errorMessage || 'Unknown backend error',
          company_id: company.id,
          user_id: access.id,
        })
      }
    } catch (error) {
      app.setProgress(null)

      track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceError, {
        device_id: variant.id,
        error_type: 'network_error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        company_id: company.id,
        user_id: access.id,
      })

      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.device_catalog.request_modal.failed_to_create_order'),
      })
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={handleClose}
      width="lg"
      title={i18n.t('it_management.device_catalog.request_modal.title')}
      primaryAction={{
        label: i18n.t('it_management.device_catalog.request_modal.send_button'),
        onClick: handleSubmit,
        disabled: !requestEmail,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleClose,
      }}
    >
      <Box flexDirection="row" gap="s24" width="full" paddingTop="s16" paddingBottom="s16">
        <Box style={{ width: '288px' }}>
          <F0Card
            image={deviceImage}
            title={deviceName}
            imageFit="contain"
            imageSize="lg"
            blurredBackground={false}
            metadata={[
              ...(price !== '-'
                ? [
                    {
                      icon: Money,
                      property: {
                        type: 'text' as const,
                        value: price,
                      },
                    },
                  ]
                : []),
              ...(period
                ? [
                    {
                      icon: Calendar,
                      property: {
                        type: 'text' as const,
                        value: period,
                      },
                    },
                  ]
                : []),
              {
                icon: Plane,
                property: {
                  type: 'text' as const,
                  value: deliveryInfo,
                },
              },
            ]}
          />
        </Box>

        <Box width="s1" background="grey300" />

        <Box gap="s16" flexDirection="column" style={{ width: '440px' }}>
          <Input
            required
            value={requestEmail}
            label={i18n.t('it_management.device_catalog.request_modal.email_label')}
            onChange={setRequestEmail}
            type="email"
            size="md"
          />
          <Box gap="s4" flexDirection="column">
            <Textarea
              value={notes}
              label={i18n.t('it_management.device_catalog.request_modal.notes_label')}
              onChange={setNotes}
            />
            <Box style={{ fontSize: '16px' }}>
              <Text size="100" weight="normal" color="grey700">
                {i18n.t('it_management.device_catalog.request_modal.notes_placeholder')}
              </Text>
            </Box>
          </Box>
          <Box
            style={{
              marginTop: '160px',
              backgroundColor: 'var(--color-blue50)',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <OneAlert
              variant="info"
              title={i18n.t('it_management.device_catalog.request_modal.info_title')}
              description={i18n.t('it_management.device_catalog.request_modal.info_description')}
              icon={Info}
            />
          </Box>
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default RequestDeviceModal
